import React, { useState } from 'react';
import { Button } from "@mui/material";
import Grid from '@mui/material/Grid2';
import { TableBase, TableEntry } from "@dndModels";
import { CHARACTER_LEVELS, CREATURE_CRS, SIMPLE_CR_OPTIONS } from "@dndModels/Constants";

import { makeStyles } from "@utilities/theme_utilities";

import AddButton from "@components/input/buttons/icon_buttons/AddButton";
import StringBox from "@components/input/strings/StringBox";
import DeleteButton from "@components/input/buttons/icon_buttons/DeleteButton";
import CheckBox from "@components/input/CheckBox";
import SelectStringBox from "@components/input/selects/SelectStringBox";
import CenteredMenu from "@components/input/menus/CenteredMenu";
import ButtonBox from "@components/input/buttons/ButtonBox";

interface Props { 
  obj: TableBase;
  disabled: boolean;
  creature?: boolean;
  fields?: string[];
  onChange: (changed: TableBase) => void;
}

export default function TableInput({
  obj,
  disabled,
  creature = false,
  fields,
  onChange,
}: Props) {
  const [expanded_entry, setExpandedEntry] = useState(-1);
  const [add_at_level, setAddAtLevel] = useState("Next");
  const [shift_by_level, setShiftByLevel] = useState(0);
  const [trickle_up, setTrickleUp] = useState(false);
  const [ping, setPing] = useState(false);
  const [view, setView] = useState("Entries");
  const [parse_string, setParseString] = useState("");
  
  const useRowStyles = makeStyles()((theme) => {
    return ({
      root: {
        backgroundColor: theme.palette.secondary.main,
        color: "white"
      },
    });
  });

  const onAdd = () => {
    // debugger;
    const new_obj = obj;
    const entry = new TableEntry();
    if (add_at_level === "Next") {
      const max_level = Math.max(...new_obj.entries.map(o => o.level), 0);
      entry.level = 1;
      const previous = new_obj.entries.find(o => o.level === max_level);
      if (previous) {
        if (previous.level > 0.5) { // It's going to be a whole number, so just increment
          entry.level = previous.level + 1;
        } else {
          const level_options = (creature ? [...SIMPLE_CR_OPTIONS] : [...CHARACTER_LEVELS]).map(o => +o).filter(o => o > previous.level);
          entry.level = Math.min(...level_options);
        }
        entry.data = {...previous.data};
      } else if (fields) {
        for (const field of fields) {
          entry.data[field] = 0;
          new_obj.field_order[new_obj.fields.length] = field;
        }
      }
    } else {
      entry.level = +add_at_level;
      const max_level = Math.max(...new_obj.entries.filter(o => o.level < entry.level).map(o => o.level), 0);
      const previous = new_obj.entries.find(o => o.level === max_level);
      if (previous) {
        entry.data = {...previous.data};
      } else {
        // It's a new lowest.  Make it match the previous lowest, and remove trickle up.
        const min_level = new_obj.entries.length === 0 ? 0 : Math.min(...new_obj.entries.map(o => o.level));
        const lowest = new_obj.entries.find(o => o.level === min_level);
        if (lowest) {
          entry.data = {...lowest.data};
          setTrickleUp(false);
        } else if (fields) {
          for (const field of fields) {
            entry.data[field] = 0;
            new_obj.field_order[new_obj.fields.length] = field;
          }
        }
      }
      setAddAtLevel("Next");
    }
    if (!isNaN(entry.level)) {
      new_obj.entries.push(entry);
      onChange(new_obj);
      setPing(!ping);
      setExpandedEntry(entry.level);
    } else {
      console.error("Logic Error.  Should never go here.");
    }
  }

  const parse = () => {
    const new_obj = obj;
    new_obj.entries = [];
    const parsed = JSON.parse(parse_string);
    if (typeof parsed === "object" && parsed.entries && parsed.entries.length > 0) {
      for (const entry of parsed.entries) {
        new_obj.entries.push(new TableEntry(entry));
      }
      if (new_obj.entries.length > 0) {
        const fields = Object.keys(new_obj.entries[0].data);
        for (let i = 0; i < fields.length; ++i) {
          new_obj.field_order[i] = fields[i];
        }
      }
    }
    onChange(new_obj);
    setPing(!ping);
  }

  const renderEntryBoxes = (entry: TableEntry) => {
    // If I make this able to support higher level table slots then I'll need to rewrite this
    if (disabled) {
      return null;
    } else {
      return (
        <Grid
          container
          spacing={0}
          direction="row"
          size={{
            xs: 12
          }}>
          { Object.keys(obj.field_order).map(o => +o).sort().map(pos => {
            const field = obj.field_order[pos];
            return (renderEntryBox(entry, pos, field));
          })}
          <Grid
            size={{
              xs: 12
            }}>
            <AddButton 
              onClick={ () => {
                const newName = "New Field";
                const new_obj = obj;
                new_obj.entries.forEach(entry2 => {
                  entry2.data[newName] = 0;
                });
                let pos = Math.max(...Object.keys(new_obj.field_order).map(o => +o), -1);
                pos++;
                new_obj.field_order[pos] = "New Field";
                onChange(new_obj);
                setPing(!ping);
              }} 
            />
          </Grid>
        </Grid>
      );
    }
  }

  const renderEntryBox = (entry: TableEntry, pos: number, field: string) => {
    return (
      <Grid
        container
        spacing={0}
        direction="row"
        key={field}
        size={{
          xs: 12
        }}>
        <Grid
          size={{
            xs: 5
          }}>
          <StringBox 
            name="Name" 
            value={field} 
            disabled={disabled}
            onBlur={(newName: string) => {
              const new_obj = obj;
              new_obj.entries.forEach(entry2 => {
                const value = entry2.data[field];
                delete entry2.data[field];
                entry2.data[newName] = value;
              });
              new_obj.field_order[pos] = newName;
              onChange(new_obj);
              setPing(!ping);
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 5
          }}>
          <StringBox 
            name="Value"
            value={`${entry.data[field]}`} 
            type="number"
            disabled={disabled}
            onBlur={(changed: string) => {
              // debugger;
              const new_obj = obj;
              // const old_value = entry.data[field];
              const diff = +changed - entry.data[field];
              entry.data[field] += diff;
              if (trickle_up) {
                for (const entry2 of new_obj.entries.filter(o => o.level > entry.level)) {
                  entry2.data[field] += diff;
                }
              }
              onChange(new_obj);
              setPing(!ping);
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 2
          }}>
          <DeleteButton
            onClick={() => {
              const new_obj = obj;
              new_obj.entries.forEach(entry2 => {
                delete entry2.data[field];
              });
              delete new_obj.field_order[pos];
              onChange(new_obj);
              setPing(!ping);
            }}
          />
        </Grid>
      </Grid>
    );
  }
  
  const { classes: rowClasses, cx: cx2 } = useRowStyles();
  // const height = window.innerHeight - 64;
  
  let level_options = creature ? [...CREATURE_CRS] : [...CHARACTER_LEVELS];
  level_options = level_options.filter(o => obj.entries.filter(o2 => `${o2.level}` === o).length === 0);
  const entry_levels = obj.entries.map(o => o.level);
  const min = entry_levels.length === 0 ? 0 : Math.min(...entry_levels);
  const max = entry_levels.length === 0 ? 0 : Math.max(...entry_levels);
  const empty_entries = level_options.map(o => +o).filter(o => o > min && o < max);
  level_options.push("Next");
  return (
    <Grid container spacing={0} direction="row">
      <Grid
        size={{
          xs: 12
        }}>
        <CenteredMenu
          options={["Entries", "Parse"]}
          selected={view}
          onSelect={setView}
        />
      </Grid>
      { view === "Entries" ?
        <>
          <Grid
            className="header"
            size={{
              xs: 4
            }}>
            Entries&nbsp;
            <AddButton
              disabled={disabled}
              onClick={onAdd}
            />
          </Grid>
          <Grid
            size={{
              xs: 4
            }}>
            <SelectStringBox
              name="Add at Level"
              options={level_options}
              value={add_at_level}
              disabled={disabled}
              onChange={setAddAtLevel}
            />
          </Grid>
          <Grid
            size={{
              xs: 4
            }}>
            <CheckBox
              name="Trickle Up"
              value={trickle_up}
              disabled={disabled}
              onChange={setTrickleUp}
            />
          </Grid>
          <Grid
            size={{
              xs: 3
            }}>
            <StringBox
              name="Shift by Level"
              value={`${shift_by_level}`}
              disabled={disabled}
              onBlur={(changed: string) => {
                setShiftByLevel(+changed);
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 3
            }}>
            <ButtonBox
              name="Shift Up"
              disabled={disabled || shift_by_level === 0}
              onClick={() => {
                const new_obj = obj;
                for (const entry of new_obj.entries) {
                  entry.level += shift_by_level;
                }
                onChange(new_obj);
                setShiftByLevel(0);
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 3
            }}>
            <ButtonBox
              name="Shift Down"
              disabled={disabled || shift_by_level === 0}
              onClick={() => {
                const new_obj = obj;
                for (const entry of new_obj.entries) {
                  entry.level -= shift_by_level;
                }
                onChange(new_obj);
                setShiftByLevel(0);
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 3
            }}>
            <ButtonBox
              name="Extrapolate Empty Entries"
              disabled={disabled || empty_entries.length === 0}
              onClick={() => {
                const new_obj = obj;
                new_obj.extrapolateEmptyFields();
                onChange(new_obj);
                setPing(!ping);
              }}
            />
          </Grid>
          { obj.entries.sort((a, b) => {
            return a.level - b.level; 
          }).map((entry, key) => {
            return (
              <Grid
                key={key}
                className="padded"
                size={{
                  xs: 12
                }}>
                <Grid container spacing={0} direction="row"
                  className={cx2(rowClasses.root)}>
                  <Grid
                    size={{
                      xs: 3
                    }}>
                    <SelectStringBox 
                      value={`${entry.level}`} 
                      name="Level"
                      disabled={disabled}
                      options={creature ? [...CREATURE_CRS] : [...CHARACTER_LEVELS]}
                      onChange={(changed: string) => {
                        const new_obj = obj;
                        entry.level = +changed;
                        onChange(new_obj);
                        setPing(!ping);
                      }}
                    />
                  </Grid>
                  <Grid
                    onClick={() => {
                      setExpandedEntry(expanded_entry === entry.level ? -1 : entry.level);
                    }}
                    size={{
                      xs: 8
                    }}>
                    { obj.entryString(entry) }
                  </Grid>
                  <Grid
                    size={{
                      xs: 1
                    }}>
                    <DeleteButton
                      onClick={() => {
                        const new_obj = obj;
                        new_obj.entries = new_obj.entries.filter(o => o.level !== entry.level);
                        onChange(new_obj);
                        setPing(!ping);
                        setExpandedEntry(-1);
                      }}
                    />
                  </Grid>
                  { expanded_entry === entry.level && renderEntryBoxes(entry) }
                </Grid>
              </Grid>
            );
          })}
        </>
      :
        <>
          <Grid
            size={{
              xs: 9
            }}>
            <StringBox 
              name="Parser"
              value={parse_string}
              multiline
              onBlur={(changed: string) => {
                while (changed.includes("–")) {
                  changed = changed.replace("–","-");
                }
                setParseString(changed);
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 3
            }}>
            <Button variant="contained" color="primary" disabled={disabled}
              onClick={parse}>
              Parse
            </Button>
          </Grid>
        </>
      }
    </Grid>
  );
}

